/**
 * A voz do sistema (spec 023, fase 2): lê em voz alta a folha da conferência
 * antes de imprimir. `speechSynthesis` do Chrome com a voz pt-BR do próprio
 * Windows (Microsoft Maria/Francisca/Thalita): sem rede, sem custo, começa na
 * hora. O texto vem pronto de `falaDe` — nada aqui passa pela IA.
 *
 * Preferência da pessoa (segue a conta, `prefsNaConta`), desligada por
 * padrão: ler ou não, a voz e a velocidade — voz que não existe no outro
 * computador cai na melhor pt-BR dele. Enquanto fala, `useFalando()` é verdadeiro — a coluna do ditado
 * usa pra calar a escuta do "Jarvis/Atlas", senão os microfones ouvem a caixa
 * de som.
 */

import { useEffect, useState, useSyncExternalStore } from 'react';

// ─── Preferência ───

const LS_KEY = 'dratento.voice.conferir.fala.v1';

export interface PrefFala {
  ler: boolean;
  /**
   * Responder por voz depois do "Imprimo?" (fase 3, chave própria — decisão
   * 5): "sim", "pula", "todas", "repete", "para". Caneta e mouse valem sempre.
   */
  confirmar: boolean;
  /** `voiceURI` escolhida; `null` = a melhor pt-BR deste computador. */
  voz: string | null;
  velocidade: number;
}

export const VELOCIDADE_PADRAO = 1.15;
const PADRAO: PrefFala = { ler: false, confirmar: false, voz: null, velocidade: VELOCIDADE_PADRAO };

function lerPref(): PrefFala {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return PADRAO;
    const p = JSON.parse(raw) as Partial<PrefFala>;
    return {
      ler: p.ler === true,
      confirmar: p.confirmar === true,
      voz: typeof p.voz === 'string' ? p.voz : null,
      velocidade: typeof p.velocidade === 'number' && p.velocidade >= 0.5 && p.velocidade <= 2 ? p.velocidade : VELOCIDADE_PADRAO,
    };
  } catch {
    return PADRAO;
  }
}

let pref: PrefFala = typeof window !== 'undefined' ? lerPref() : PADRAO;
const ouvintesPref = new Set<() => void>();

if (typeof window !== 'undefined') {
  // Outra aba mudou a preferência.
  window.addEventListener('storage', (e) => {
    if (e.key !== LS_KEY) return;
    pref = lerPref();
    ouvintesPref.forEach((f) => f());
  });
}

export function getPrefFala(): PrefFala {
  return pref;
}

export function setPrefFala(parcial: Partial<PrefFala>): void {
  pref = { ...pref, ...parcial };
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(pref));
  } catch {
    /* storage bloqueado: vale só nesta sessão */
  }
  ouvintesPref.forEach((f) => f());
}

export function usePrefFala(): PrefFala {
  return useSyncExternalStore(
    (f) => {
      ouvintesPref.add(f);
      return () => ouvintesPref.delete(f);
    },
    () => pref,
    () => PADRAO,
  );
}

// ─── Vozes ───

const sintese = (): SpeechSynthesis | null =>
  typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined'
    ? window.speechSynthesis
    : null;

/** Vozes em português: pt-BR antes de pt-PT, as do computador (sem rede) antes das online. */
export function vozesPt(): SpeechSynthesisVoice[] {
  const s = sintese();
  if (!s) return [];
  const nota = (v: SpeechSynthesisVoice) => (v.lang.toLowerCase().replace('_', '-') === 'pt-br' ? 0 : 2) + (v.localService ? 0 : 1);
  return s
    .getVoices()
    .filter((v) => v.lang.toLowerCase().startsWith('pt'))
    .sort((a, b) => nota(a) - nota(b) || a.name.localeCompare(b.name));
}

/** O Chrome entrega a lista de vozes depois (`voiceschanged`). */
export function useVozesPt(): SpeechSynthesisVoice[] {
  const [vozes, setVozes] = useState(vozesPt);
  useEffect(() => {
    const s = sintese();
    if (!s) return;
    const atualizar = () => setVozes(vozesPt());
    s.addEventListener?.('voiceschanged', atualizar);
    atualizar();
    return () => s.removeEventListener?.('voiceschanged', atualizar);
  }, []);
  return vozes;
}

function vozEscolhida(uri: string | null): SpeechSynthesisVoice | null {
  const vozes = vozesPt();
  return (uri && vozes.find((v) => v.voiceURI === uri)) || vozes[0] || null;
}

// Pede a lista já ao abrir a página: o Chrome só começa a carregar as vozes
// na primeira `getVoices()` e entrega depois (`voiceschanged`).
if (typeof window !== 'undefined') sintese()?.getVoices();

/** Teto pra esperar a voz escolhida aparecer na lista (ms). */
const ESPERA_VOZ_MS = 1500;
/** A espera já estourou uma vez (voz que saiu do computador): não espera mais. */
let semEspera = false;
/** Desiste da espera em andamento — a fala foi cortada antes de começar. */
let largarEspera: (() => void) | null = null;

/**
 * Segue quando a voz escolhida está na lista. O Chrome carrega as vozes
 * depois — as online (Google) por último —, e falar antes sai na primeira da
 * Microsoft: foi o nome do próximo paciente (23/10/2026), primeira fala da
 * página depois de recarregar. Pronta, segue na hora; senão espera o
 * `voiceschanged`, com teto pra voz que sumiu não calar a fala.
 */
function quandoVozPronta(s: SpeechSynthesis, uri: string | null, seguir: () => void, desistiu: () => void): void {
  const pronta = () => (uri ? vozesPt().some((v) => v.voiceURI === uri) : s.getVoices().length > 0);
  if (semEspera || pronta()) return seguir();
  const soltar = () => {
    clearTimeout(teto);
    s.removeEventListener?.('voiceschanged', mudou);
    largarEspera = null;
  };
  const mudou = () => {
    if (!pronta()) return;
    soltar();
    seguir();
  };
  const teto = setTimeout(() => {
    semEspera = true;
    soltar();
    seguir();
  }, ESPERA_VOZ_MS);
  s.addEventListener?.('voiceschanged', mudou);
  largarEspera = () => {
    soltar();
    desistiu();
  };
}

// ─── Falar ───

let falando = false;
let rodada = 0;
const ouvintesFalando = new Set<() => void>();

function setFalando(next: boolean): void {
  if (falando === next) return;
  falando = next;
  ouvintesFalando.forEach((f) => f());
}

export function useFalando(): boolean {
  return useSyncExternalStore(
    (f) => {
      ouvintesFalando.add(f);
      return () => ouvintesFalando.delete(f);
    },
    () => falando,
    () => false,
  );
}

/** Corta o que estiver sendo dito (caneta no meio da leitura, folha nova). */
export function pararFala(): void {
  rodada++;
  largarEspera?.();
  sintese()?.cancel();
  setFalando(false);
}

/** Como a fala acabou: até o fim, cortada (caneta, folha nova) ou sem voz/erro. */
export type FimFala = 'fim' | 'cortada' | 'falhou';

/**
 * Fala o texto, frase por frase (o Chrome corta enunciados longos). Quem
 * pergunta depois ("Imprimo?") só desiste se foi `cortada`: sem voz neste
 * computador, a pergunta vale do mesmo jeito.
 */
export function falar(texto: string, p: PrefFala = getPrefFala()): Promise<FimFala> {
  const s = sintese();
  pararFala();
  const frases = texto.split(/(?<=[.?!])\s+/).map((f) => f.trim()).filter(Boolean);
  if (!s || frases.length === 0) return Promise.resolve('falhou');
  const minha = rodada;
  setFalando(true);
  return new Promise((resolve) => {
    const fim = (ok: boolean) => {
      if (minha !== rodada) return resolve('cortada');
      setFalando(false);
      resolve(ok ? 'fim' : 'falhou');
    };
    const dizer = () => {
      const voz = vozEscolhida(p.voz);
      frases.forEach((frase, i) => {
        const u = new SpeechSynthesisUtterance(frase);
        u.lang = voz?.lang ?? 'pt-BR';
        if (voz) u.voice = voz;
        u.rate = p.velocidade;
        if (i === frases.length - 1) u.onend = () => fim(true);
        u.onerror = (e) => {
          if (e.error !== 'interrupted' && e.error !== 'canceled') console.warn('[voz] síntese falhou:', e.error);
          fim(false);
        };
        s.speak(u);
      });
    };
    // Cortada enquanto esperava a voz: não fala.
    quandoVozPronta(s, p.voz, dizer, () => resolve('cortada'));
  });
}
